import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { LogOut, Home, Shield, Menu, X, Eye, CreditCard, Lock } from 'lucide-react';
import CreditsChip from '@/components/CreditsChip';
import { useCredits } from '@/contexts/CreditsContext';
import ThemeToggle from '@/components/ThemeToggle';
import InstallPFDButton from '@/components/InstallPFDButton';

export default function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isUnlimited } = useCredits();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isDemo = user?.is_demo === true;
  const isReadOnly = user?.is_read_only === true;
  const isRestrictedDemo = user?.is_restricted_demo === true;

  const handleLogout = () => {
    logout();
    navigate('/auth');
    setMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
    <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-50 shadow-sm transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Desktop Header - Hidden on mobile */}
        <div className="hidden md:flex justify-between items-center h-20">
          {/* Logo Section */}
          <div className="flex items-center space-x-4">
            <img 
              src="/tps-logo.png" 
              alt="Total Production Solutions" 
              className="h-12 w-auto cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/dashboard')}
            />
            <div className="border-l border-gray-300 dark:border-slate-600 h-8"></div>
            <h1 className="text-xl font-semibold text-gray-800 dark:text-slate-100">
              Precision Fish Detection
            </h1>
          </div>

          {/* Right Section - User Info and Logout */}
          {user && (
            <div className="flex items-center space-x-4">
              <ThemeToggle size="sm" className="hidden lg:inline-flex" />
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-gray-700 dark:text-slate-200 hover:text-[#0891b2] dark:hover:text-cyan-400 hover:bg-gray-100 dark:hover:bg-slate-800"
              >
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              
              {/* Admin Console Button - Only for admin users */}
              {user.is_admin && (
                <Button
                  onClick={() => navigate('/admin')}
                  variant="ghost"
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  data-testid="admin-console-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin Console
                </Button>
              )}
              
              <div className="flex items-center space-x-3">
                <InstallPFDButton />
                <CreditsChip />
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{user.email}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    {user.is_unlimited ? 'Unlimited Access' : `${user.free_analysis_credits || 0} analyses left`}
                  </p>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Header - Visible only on mobile */}
        <div className="md:hidden">
          {/* Top row: Logo + Credits Chip + Menu button */}
          <div className="flex justify-between items-center h-16 gap-2">
            <img 
              src="/tps-logo.png" 
              alt="Total Production Solutions" 
              className="h-10 w-auto cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
              onClick={() => handleNavigation('/dashboard')}
            />
            
            {user && (
              <div className="flex items-center gap-2">
                <CreditsChip />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2"
                  aria-label="Toggle menu"
                  data-testid="mobile-menu-toggle"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6 text-gray-700" />
                  ) : (
                    <Menu className="h-6 w-6 text-gray-700" />
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Title row - Centered, two lines */}
          <div className="text-center pb-3 border-b border-gray-100 dark:border-slate-700">
            <h1 className="text-lg font-semibold text-gray-800 dark:text-slate-100 leading-tight">
              <span className="block">Precision</span>
              <span className="block">Fish Detection</span>
            </h1>
            {user && <div className="mt-2 flex justify-center"><ThemeToggle size="sm" /></div>}
          </div>

          {/* Mobile Menu - Expandable */}
          {user && mobileMenuOpen && (
            <div className="py-3 space-y-2 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
              {/* User Info */}
              <div className="px-2 py-2 text-center border-b border-gray-200 dark:border-slate-700 mb-2">
                <p className="text-sm font-medium text-gray-800 dark:text-slate-100 truncate">{user.email}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {user.is_unlimited ? 'Unlimited Access' : `${user.free_analysis_credits || 0} analyses left`}
                </p>
              </div>

              {/* Navigation buttons */}
              <Button
                onClick={() => handleNavigation('/dashboard')}
                variant="ghost"
                className="w-full justify-start text-gray-700 hover:text-[#0891b2] hover:bg-gray-100 py-3"
              >
                <Home className="w-5 h-5 mr-3" />
                Dashboard
              </Button>

              {/* Billing entry — hidden for unlimited (regular) users; only
                  force_credit_charging accounts see credits at all. */}
              {!isUnlimited && (
                <Button
                  onClick={() => handleNavigation('/billing')}
                  variant="ghost"
                  className="w-full justify-start text-[#0891b2] hover:text-[#0891b2] hover:bg-cyan-50 py-3"
                  data-testid="mobile-billing-btn"
                >
                  <CreditCard className="w-5 h-5 mr-3" />
                  Billing & Credits
                </Button>
              )}
              
              {/* Admin Console Button - Only for admin users */}
              {user.is_admin && (
                <Button
                  onClick={() => handleNavigation('/admin')}
                  variant="ghost"
                  className="w-full justify-start text-purple-600 hover:text-purple-700 hover:bg-purple-50 py-3"
                  data-testid="admin-console-btn-mobile"
                >
                  <Shield className="w-5 h-5 mr-3" />
                  Admin Console
                </Button>
              )}
              
              {/* Logout */}
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 py-3"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
    {isDemo && (
      <div className="bg-amber-500 text-white text-center py-1.5 px-4 text-xs sm:text-sm font-semibold sticky top-[64px] md:top-[80px] z-40 shadow-sm" data-testid="demo-banner">
        <Eye className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
        Demonstration version only
      </div>
    )}
    {isRestrictedDemo && (
      <div className="bg-slate-900 text-amber-300 text-center py-1.5 px-4 text-xs sm:text-sm font-semibold sticky top-[64px] md:top-[80px] z-40 shadow-sm" data-testid="restricted-demo-banner">
        <Lock className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
        Restricted demo · view only — editing, uploads, exports and screen capture are disabled.
      </div>
    )}
    {isReadOnly && !isDemo && !isRestrictedDemo && (
      <div className="bg-amber-500 text-white text-center py-1.5 px-4 text-xs sm:text-sm font-semibold sticky top-[64px] md:top-[80px] z-40 shadow-sm" data-testid="read-only-account-banner">
        <Eye className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
        Your account is read-only. Contact support for full access.
      </div>
    )}
    </>
  );
}
